import { useContext, useCallback } from "react";
import { ConfiguratorContext } from "../context";

/**
 * Contador de peticiones compartido por todas las instancias del hook,
 * para descartar respuestas fuera de orden.
 */
let kintoRequestSeq = 0;

export const nextKintoRequestId = () => ++kintoRequestSeq;

/**
 * Hook para simulación de datos de Kinto
 * Maneja configuración de pie, tiempo y kilometraje para simulaciones de Kinto
 */
const useKintoSimulationData = () => {
    const [state, setState] = useContext(ConfiguratorContext);

    const kintoData = state.kintoSimulationData;

    // Actualiza los datos de simulación Kinto
    const updateKintoData = useCallback(
        (newData) => {
            setState((prevState) => ({
                ...prevState,
                kintoSimulationData: {
                    ...prevState.kintoSimulationData,
                    ...newData,
                },
            }));
        },
        [setState]
    );

    // Actualiza solo el porcentaje de pie
    const updateDownpayment = useCallback(
        (downpayment) => {
            updateKintoData({ downpayment });
        },
        [updateKintoData]
    );

    // Actualiza solo el tiempo (meses)
    const updateTime = useCallback(
        (time) => {
            updateKintoData({ time });
        },
        [updateKintoData]
    );

    // Actualiza solo los kilómetros anuales
    const updateKilometers = useCallback(
        (kilometers) => {
            updateKintoData({ kilometers });
        },
        [updateKintoData]
    );

    // Marca que la simulación tiene resultado
    const setHasResult = useCallback(
        (hasResult, resultData = null) => {
            updateKintoData({ hasResult, resultData, error: null });
        },
        [updateKintoData]
    );

    /**
     * Aplica el desenlace de una simulación sólo si sigue siendo relevante:
     * descarta respuestas de una versión que el usuario ya cambió y respuestas
     * superadas por una petición posterior.
     */
    const applyKintoResult = useCallback(
        (versionId, requestId, newData) => {
            setState((prevState) => {
                if (prevState.selectedVersion?.id !== versionId) return prevState;
                if ((prevState.kintoSimulationData.request_id || 0) > requestId) return prevState;

                return {
                    ...prevState,
                    kintoSimulationData: {
                        ...prevState.kintoSimulationData,
                        ...newData,
                        request_id: requestId,
                    },
                };
            });
        },
        [setState]
    );

    // Controla el estado de carga de la simulación
    const setIsLoading = useCallback(
        (isLoading) => {
            updateKintoData({ isLoading });
        },
        [updateKintoData]
    );

    // Establece un error en la simulación
    const setError = useCallback(
        (error) => {
            updateKintoData({ 
                error, 
                hasResult: false, 
                resultData: null, 
                isLoading: false 
            });
        },
        [updateKintoData]
    );

    // Limpia el error
    const clearError = useCallback(() => {
        updateKintoData({ error: null });
    }, [updateKintoData]);

    // Resetear para edición (mantener los valores pero ocultar resultado).
    // Se conserva `simulated_for` para saber a qué configuración correspondía
    // el último resultado simulado.
    const resetForEdit = useCallback(() => {
        updateKintoData({ hasResult: false, resultData: null, isLoading: false, error: null });
    }, [updateKintoData]);

    // Obtener datos actuales
    const getKintoData = useCallback(() => {
        return kintoData;
    }, [kintoData]);

    return {
        kintoData,
        updateKintoData,
        updateDownpayment,
        updateTime,
        updateKilometers,
        setHasResult,
        applyKintoResult,
        setIsLoading,
        setError,
        clearError,
        resetForEdit,
        getKintoData,
    };
};

export default useKintoSimulationData;
